import React, { useState } from 'react'
import { Button } from 'primereact/button'

import { PaymentBillableConnectorsSummary, PaymentProductDisplayResponse } from '../../../common/redux/api/web-api'
import useText from '../../../common/hooks/useText'
import {
    SubscriptionCardLayout,
    SubscriptionMarketingFeature,
} from '../subscription-card-layout/subscription-card-layout'
import { useLocalizationContext } from '../../../common/components/localization-provider'
import { SelectButton } from '../../../common/uikit/SelectButton'

import { buildSubscriptionCardWidgetViewModel, type BillingInterval } from './subscription-card-widget-model'

export type { BillingInterval } from './subscription-card-widget-model'

const BILLING_INTERVALS = ['month', 'year'] as const satisfies readonly BillingInterval[]

const getAvailableBillingIntervals = (product: PaymentProductDisplayResponse) =>
    BILLING_INTERVALS.filter((interval) => product.prices?.some((price) => price.interval === interval))

export type SubscriptionCardWidgetProps = {
    product: PaymentProductDisplayResponse
    currency: string
    billableConnectors?: PaymentBillableConnectorsSummary | null
    marketingFeatures: SubscriptionMarketingFeature[]
    onSelectPlan: (selectedBillingInterval: BillingInterval) => void
}

export function SubscriptionCardWidget({
    product,
    currency,
    onSelectPlan,
    billableConnectors,
    marketingFeatures,
}: SubscriptionCardWidgetProps) {
    const text = useText()
    const { locale } = useLocalizationContext()
    const [selectedBillingInterval, setSelectedBillingInterval] = useState<BillingInterval>('month')
    const isPro = product.name.toLowerCase() === 'pro'
    const availableBillingIntervals = getAvailableBillingIntervals(product)
    const showBillingIntervalSelector = isPro && availableBillingIntervals.length > 1
    const billingInterval = showBillingIntervalSelector
        ? selectedBillingInterval
        : availableBillingIntervals[0] ?? 'month'

    const model = buildSubscriptionCardWidgetViewModel({
        product,
        billableConnectors,
        marketingFeatures,
        billingInterval,
        text,
        locale,
        currency,
    })

    const billingIntervalSelector = showBillingIntervalSelector ? (
        <div className="flex justify-content-center">
            <SelectButton<BillingInterval>
                id={`subscription-${product.id}-interval`}
                ariaLabel={text('lblBillingInterval')}
                value={billingInterval}
                onChange={setSelectedBillingInterval}
                options={[
                    { value: 'month' as const, label: text('lblPayMonthly') },
                    { value: 'year' as const, label: text('lblPayYearly') },
                ]}
                className="inline-flex align-items-center border-round-sm border-1 surface-0 surface-border px-1 py-1 gap-2"
                style={{ minWidth: '16rem', width: 'fit-content' }}
                data-testid={`subscription-${product.id}-interval-selector`}
                optionContentClassName="text-sm font-bold"
                optionLabelClassName="text-sm font-bold"
                optionTestId={(option) => `subscription-${product.id}-interval-${String(option.value)}`}
                optionClassName={(_option, { selected }) =>
                    [
                        'flex-1 justify-content-center border-none border-round-sm py-2 px-4 cursor-pointer transition-duration-150 transition-colors shadow-none',
                        selected ? 'bg-green-300 text-900 shadow-1' : 'bg-transparent text-700 hover:surface-100',
                    ].join(' ')
                }
            />
        </div>
    ) : null

    return (
        <SubscriptionCardLayout
            testId={`subscription-${product.id}-card`}
            title={model.title}
            description={model.description}
            headerContent={billingIntervalSelector}
            isRecommended={model.isRecommended}
            tag={model.tag}
            discountInPercent={model.discountInPercent}
            price={model.primaryPrice.amount}
            priceSuffix={model.primaryPrice.suffix}
            priceDetailLines={model.detailLines}
            marketingFeatures={model.marketingFeatures}
            footer={
                model.footer.type === 'cta' ? (
                    <Button
                        className="w-full text-xl radius-small"
                        label={model.footer.text}
                        severity="success"
                        onClick={() => onSelectPlan(billingInterval)}
                        disabled={model.footer.isDisabled}
                    />
                ) : (
                    <p>{model.footer.text}</p>
                )
            }
        />
    )
}
